import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { forkJoin, Observable } from 'rxjs';
import {
  GroupIncentiveParameter,
  ReportIncentiveParameter,
  ReportNr1,
  ReportSalespersonBonusParameter
} from '../../state/models/reports';
import { UtilsService } from './utils.service';
import { OrganizationType } from '../../state/models/organization';
import { OrganizationService } from './organization.service';

export type MetabaseNames =
  'dados-gerais' |
  'dados-gerais-grupo' |
  'relatorio-financeiro' |
  'relatorio-financeiro-grupo' |
  'saude' |
  'saude-grupo' |
  'dados-cadastrais' |
  'dados-cadastrais-corretor' |
  'adesao-operadora' |
  'custos-prevencao' |
  'saude-operadora' |
  'exposicao-risco-financeiro' |
  'receita-contrato-incentivado' |
  'adesao-operadora-corretor' |
  'custos-prevencao-corretor' |
  'saude-operadora-corretor' |
  'satisfacao';


@Injectable({
  providedIn: 'root',
})
export class ReportsService {

  private metabaseConfig = {
    ...this.metabaseConfigBroker,
    ...this.metabaseConfigHealthOperator,
    ...this.metabaseConfigHumanResources,
    ...this.metabaseConfigGroup,
  };

  get metabaseConfigBroker() {
    return {
      'dados-cadastrais-corretor': {
        id: 123,
        title: 'Dados cadastrais',
        shortLabel: 'Dados cadastrais',
        icon: 'groups',
        url: 'dados-cadastrais-corretor',
        minSubscriptions: 0,
        acl: ['BROKER']
      },
      'adesao-operadora-corretor': {
        id: 121,
        title: 'Adesão',
        icon: 'tornado_outlined',
        url: 'adesao-operadora-corretor',
        minSubscriptions: 0,
        acl: ['BROKER']
      },
      'custos-prevencao-corretor': {
        id: 122,
        title: 'Custo com Prevenção',
        shortLabel: 'Custo',
        icon: 'attach_money',
        url: 'custos-prevencao-corretor',
        minSubscriptions: 0,
        acl: ['BROKER']
      },
      'saude-operadora-corretor': {
        id: 124,
        title: 'Saúde',
        icon: 'monitor_heart_outlined',
        url: 'saude-operadora-corretor',
        minSubscriptions: 0,
        acl: ['BROKER']
      },
    };
  }

  get metabaseConfigHealthOperator() {
    return {
      'dados-cadastrais': {
        id: 112,
        title: 'Dados cadastrais',
        shortLabel: 'Dados cadastrais',
        icon: 'groups',
        url: 'dados-cadastrais',
        minSubscriptions: 0,
        acl: ['HEALTH_OPERATOR']
      },
      'adesao-operadora': {
        id: 114,
        title: 'Adesão',
        icon: 'tornado_outlined',
        url: 'adesao-operadora',
        minSubscriptions: 0,
        acl: ['HEALTH_OPERATOR']
      },
      'custos-prevencao': {
        id: 111,
        title: 'Custo com Prevenção',
        shortLabel: 'Custo',
        icon: 'attach_money',
        url: 'custos-prevencao',
        minSubscriptions: 0,
        acl: ['HEALTH_OPERATOR']
      },
      'saude-operadora': {
        id: 113,
        title: 'Saúde',
        icon: 'monitor_heart_outlined',
        url: 'saude-operadora',
        minSubscriptions: 0,
        acl: ['HEALTH_OPERATOR']
      },
    };
  }

  get metabaseConfigHumanResources() {
    return {
      'dados-gerais': {
        id: 118,
        title: 'Dados cadastrais',
        shortLabel: '',
        icon: 'groups',
        url: 'dados-gerais',
        minSubscriptions: 30,
        acl: ['HUMAN_RESOURCES'],
      },
      'relatorio-financeiro': {
        id: 120,
        title: 'Financeiro',
        icon: 'attach_money',
        url: 'relatorio-financeiro',
        minSubscriptions: 0,
        acl: ['HUMAN_RESOURCES']
      },
      'saude': {
        id: 119,
        title: 'Saúde',
        icon: 'monitor_heart_outlined',
        url: 'saude',
        minSubscriptions: 30, //30
        acl: ['HUMAN_RESOURCES']
      },
    };
  }

  get metabaseConfigGroup() {
    return {
      'dados-gerais-grupo': {
        id: 125,
        title: 'Dados cadastrais',
        shortLabel: '',
        icon: 'groups',
        url: 'dados-gerais-grupo',
        minSubscriptions: 30,
        acl: ['GROUP'],
      },
      'relatorio-financeiro-grupo': {
        id: 127,
        title: 'Financeiro',
        icon: 'attach_money',
        url: 'relatorio-financeiro-grupo',
        minSubscriptions: 0,
        acl: ['GROUP']
      },
      'saude-grupo': {
        id: 126,
        title: 'Saúde',
        icon: 'monitor_heart_outlined',
        url: 'saude-grupo',
        minSubscriptions: 30, //30
        acl: ['GROUP']
      },
    };
  }

  constructor(

    private organizationService: OrganizationService,
    private _httpClient: HttpClient, private utilsService: UtilsService) {
  }

  private getMetabaseProperty(url: MetabaseNames, property: keyof typeof this.metabaseConfig['dados-gerais']): string | number | string[] {
    return this.metabaseConfig[url][property];
  }

  public getReportId(name: string): string {
    return `METABASE_${ this.getConfigByUrl(name) }`;
  }

  public getReportTitle(name: MetabaseNames): string {
    return this.getMetabaseProperty(name, 'title') as string;
  }

  public getReportShortLabel(name: MetabaseNames): string {
    const shortLabel = this.getMetabaseProperty(name, 'shortLabel');
    if (shortLabel) {
      return shortLabel as string;
    }
    return this.getMetabaseProperty(name, 'title') as string;
  }

  public getReportIcon(name: MetabaseNames): string {
    return this.getMetabaseProperty(name, 'icon') as string;
  }

  public getReportMinSubscription(name: MetabaseNames): number {
    return this.getMetabaseProperty(name, 'minSubscriptions') as number;
  }

  public getReportUrlId(name: MetabaseNames): string {
    return this.getMetabaseProperty(name, 'url') as string;
  }

  public getReportAcl(name: MetabaseNames): OrganizationType[] {
    return this.getMetabaseProperty(name, 'acl') as OrganizationType[];
  }

  public getReportUrl(name: MetabaseNames): string {
    return `/organizacoes/organizationId/estatisticas/${ this.getReportUrlId(name) }`;
  }

  public getConfigByUrl(url: string) {
    for (const key in this.metabaseConfig) {
      if (this.metabaseConfig[key].url === url) {
        return this.metabaseConfig[key].id;
      }
    }
    return null; // Retorna null se a URL não for encontrada
  }

  getListAllReportsOptions(aclList: string[]) {
    const options: any[] = [];
    Object.keys(this.metabaseConfig).forEach((key: any) => {
      const acl: string[] = this.getReportAcl(key);
      const allow: boolean = aclList.some(aclItem => acl.includes(aclItem));
      if (allow) {
        options.push({
          value: this.getMetabaseProperty(key, 'url'),
          label: this.getReportTitle(key),
          icon: this.getReportIcon(key),
          minSubscriptions: this.getReportMinSubscription(key),
        });
      }
    });
    return options;
  }

  getReportByOrganizationIdReportName(organizationId: any, metabaseIdName: string): Observable<any> {
    const body = {
      report: {
        reportTemplate: {
          name: this.getReportId(metabaseIdName),
        },
        organization: {
          id: organizationId,
        },
      },
    };
    return this.getReport(body);
  }

  hasAnyInGroupWithIncentive(){

  }

  getGroupIncentive(organizationId: any): Observable<GroupIncentiveParameter[]> {
    const body = {
      report: {
        reportTemplate: {
          name: 'GROUP_INCENTIVE_ORGANIZATION_LIST',
        },
        organization: {
          id: organizationId,
        },
      },
    };
    return this.getReport(body).pipe(
      map(response => response?.results ?? [])
    );
  }


  getReportSalesPerson(organizationId: number): Observable<ReportSalespersonBonusParameter[]> {
    const body = {
      report: {
        reportTemplate: {
          name: 'SALESPERSON_ORGANIZATION_LIST',
        },
        organization: {
          id: organizationId,
        },
      },
    };
    return this.getReport(body).pipe(
      map(response => response?.results)
    );
  }

  getReportIncentive(organizationId: number, cycleId: number): Observable<ReportIncentiveParameter[]> {
    const body = {
      report: {
        reportTemplate: {
          name: 'INCENTIVE_PARAMETER',
        },
        organization: {
          id: organizationId,
        },
        reportParams: { cycleId }
      },
    };
    return this.getReport(body).pipe(
      map(response => response?.results)
    );
  }

  getCheckPreviousYear(organizationId: number, date: string): Observable<any> {
    const reports = [
      'PSYCHOSOCIAL_REPORT_CYCLES',
    ].map(name => {
      const body = {
        report: {
          reportTemplate: {
            name,
          },
          organization: {
            id: organizationId,
          },
          reportParams: {
            date
          }
        },
      };
      return this.getReport(body).pipe(
        map(response => this.utilsService.convertKeysToCamelCase(response?.results))
      );
    });

    return forkJoin(reports).pipe(
      map(results => {
        const [psychosocialReportCycle] = results;
        return psychosocialReportCycle;
      })
    );
  }

  getReportNR1(organizationId: number, date: string): Observable<ReportNr1> {
    const reports = [
      'PSYCHOSOCIAL_REPORT_TOTALS',
      'PSYCHOSOCIAL_REPORT_SUMMARY_DATA',
      'PSYCHOSOCIAL_REPORT_BY_CONDITION',
      'PSYCHOSOCIAL_REPORT_LISTED_DEPARTMENTS',
      'PSYCHOSOCIAL_REPORT_RISK_MAP',
      'PSYCHOSOCIAL_REPORT_DEPARTMENT_ILLNESS_RATE',
      'PSYCHOSOCIAL_REPORT_BRANCH_ILLNESS_RATE',
      'PSYCHOSOCIAL_REPORT_ADHERENCE',
      'PSYCHOSOCIAL_REPORT_FIELD_ILLNESS_RATE',
      'PSYCHOSOCIAL_REPORT_AVG_ILLNESS_RATE'

    ].map(name => {
      const body = {
        report: {
          reportTemplate: {
            name,
          },
          organization: {
            id: organizationId,
          },
          reportParams: {
            date
          }
        },
      };
      return this.getReport(body).pipe(
        map(response => response?.results)
      );
    });
    return forkJoin(reports).pipe(
      map(response => {
        const [
          totals,
          summaryData,
          byCondition,
          listedDepartments,
          riskMap,
          departmentIllnessRate,
          branchIllnessRate,
          adherence,
          fieldIllnessRate,
          avgIllnessRate
        ] = response;
        return {
          totals,
          summaryData,
          byCondition,
          listedDepartments,
          riskMap,
          departmentIllnessRate,
          branchIllnessRate,
          adherence,
          fieldIllnessRate,
          avgIllnessRate
        };
      })
    );
  }

  getReportNR1AdherenceWorkRelationsTest(
    organizationId: number,
    date: string,
    workRelations: ('EMPLOYEE' | 'CONTRACTOR' | 'PARTNER' | 'INTERN')[] | null
  ): Observable<any> {
    const body = {
      report: {
        reportTemplate: {
          name: 'PSYCHOSOCIAL_REPORT_ADHERENCE_WORK_RELATIONS_TEST',
        },
        organization: {
          id: organizationId,
        },
        reportParams: {
          date,
          workRelations,
        },
      },
    };

    return this.getReport(body).pipe(
      map(response => response?.results)
    );
  }

  getSubmittedFormsList(
    organizationId: number,
    startDate: string,
    endDate: string
  ): Observable<{ results: Record<string, unknown>[] }> {
    const body = {
      report: {
        reportTemplate: {
          name: 'SUBMITTED_FORMS_LIST',
        },
        organization: {
          id: organizationId,
        },
        reportParams: {
          startDate,
          endDate,
        },
      },
    };
    return this.getReport(body).pipe(
      map(response => ({
        results: response?.results ?? [],
      }))
    );
  }

  getSubmittedFormsCount(
    organizationId: number,
    startDate: string,
    endDate: string
  ): Observable<number> {
    const body = {
      report: {
        reportTemplate: {
          name: 'SUBMITTED_FORMS_COUNT',
        },
        organization: {
          id: organizationId,
        },
        reportParams: {
          startDate,
          endDate,
        },
      },
    };

    return this.getReport(body).pipe(
      map((response) => {
        const results = response?.results;
        if (typeof results === 'number') {
          return results;
        }

        if (Array.isArray(results) && results.length) {
          const [first] = results;
          if (typeof first === 'number') {
            return first;
          }
          if (first && typeof first === 'object') {
            const numericValue = Object.values(first).find((value) => typeof value === 'number');
            return typeof numericValue === 'number' ? numericValue : 0;
          }
        }

        if (results && typeof results === 'object') {
          const numericValue = Object.values(results).find((value) => typeof value === 'number');
          return typeof numericValue === 'number' ? numericValue : 0;
        }

        return 0;
      })
    );
  }

  getReport(body: any): Observable<any> {
    const _baseUrlReports = `${ environment.apiUrl }/reports`;
    return this._httpClient.post(_baseUrlReports, body).pipe(
      map((response: any) => response?.report),
      catchError(err => {
        this.utilsService.setErrorToast(err);
        throw new Error(err);
      })
    );
  }


}
